/**
 * Visit and event-related types.
 */

import { BaseEntity, Coordinates, DateField } from './base';
import { Location, LocationType } from './account';
import { Supervisor } from './supervisor';
import { Parent, Child } from './case';
import { ServiceType } from './demographics';

// ============================================================================
// EVENT TYPE (defined first for VisitStatus to use)
// ============================================================================

export type NoteType = "observation" | "interaction" | "incident" | "intervention" | "phone_call" | "party_arrived" | "handoff_note";

export type EventType = NoteType |
  "supervisor_started"
  | "supervisor_arrived"
  | "supervisor_enroute"
  | "supervisor_no_show"
  | "supervisor_ended";

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  supervisor_started: "Supervisor Started",
  supervisor_arrived: "Supervisor Arrived",
  supervisor_enroute: "Supervisor En Route",
  supervisor_no_show: "Supervisor No Show",
  supervisor_ended: "Supervisor Ended",
  observation: "Observation",
  interaction: "Interaction",
  incident: "Incident",
  intervention: "Intervention",
  phone_call: "Phone Call",
  party_arrived: "Arrival",
  handoff_note: "Handoff Notes",
};

// ============================================================================
// VISIT
// ============================================================================

export type InvitationStatus = 'pending' | 'accepted' | 'declined' | 'change_requested' | 'confirmed' | 'denied';

export interface VisitInvitationChangeRequest {
  reason: string;
  suggestedDate?: DateField;
  suggestedTime?: string;
}

export interface VisitInvitation {
  status: InvitationStatus;
  sentAt?: DateField;
  respondedAt?: DateField;
  responseMessage?: string;
  changeRequest?: VisitInvitationChangeRequest;
}

/** Attendance tracking for visit participants */
export interface VisitAttendance {
  /** Parent IDs marked as present */
  parentIds?: string[];
  /** Child IDs marked as present */
  childIds?: string[];
  /** Total number of participants (including parents, children, and any others) */
  participantCount?: number;
  /** Additional attendees not tracked as parents/children (e.g., support persons, observers) */
  additionalAttendees?: number;
  /** When attendance was recorded */
  recordedAt?: DateField;
  /** User ID of who recorded the attendance (typically the supervisor) */
  recordedBy?: string;
}

/** Party responsible for a no-show */
export type NoShowParty = 'visiting_parent' | 'custodial_parent' | 'both_parents' | 'child' | 'supervisor' | 'other';

export const NO_SHOW_PARTY_LABELS: Record<NoShowParty, string> = {
  visiting_parent: 'Visiting Parent',
  custodial_parent: 'Custodial Parent',
  both_parents: 'Both Parents',
  child: 'Child',
  supervisor: 'Supervisor',
  other: 'Other',
};

/** Details about a no-show visit */
export interface NoShowDetails {
  /** Who didn't show up */
  noShowParty: NoShowParty;
  /** Optional notes about the no-show */
  notes?: string;
  /** When the no-show was recorded */
  recordedAt?: DateField;
  /** User ID of who recorded the no-show */
  recordedBy?: string;
}

export interface Visit extends BaseEntity {
  caseId: string;
  startDate: DateField;
  endDate: DateField;
  locationId?: string;
  location?: Location;
  status: VisitStatus;
  supervisorId?: string;
  supervisor?: Supervisor;
  parents?: Record<string, Parent>;
  parentIds?: string[];
  children?: Record<string, Child>;
  childIds?: string[];
  accountId?: string;
  createdBy?: string;
  reportId?: string;
  pdfReportId?: string;
  eventReportPath?: string;
  /** Pre-visit notes/instructions set by admin during visit planning */
  notes?: string;
  cancelationReason?: string;
  timeZoneId?: string;
  systemBilled?: boolean;
  nylasEventId?: string;
  parentInvitations?: Record<string, VisitInvitation>;
  /** The type of service being provided during this visit */
  serviceType?: ServiceType;
  /** Post-visit notes added after the visit ends (e.g., observations as participants were leaving) */
  postVisitNotes?: string;
  /** When post-visit notes were added */
  postVisitNotesAddedAt?: DateField;
  /** User ID of who added the post-visit notes */
  postVisitNotesAddedBy?: string;
  /** Attendance tracking - which participants were present at the visit */
  attendance?: VisitAttendance;
  /** No-show details - who didn't show up and why (only set when status is no_show) */
  noShowDetails?: NoShowDetails;

  // Payment tracking (visit/client payment)
  /** Payment status for the visit */
  paymentStatus?: VisitPaymentStatus;
  /** Date when payment was received */
  paymentDate?: DateField;
  /** URL to uploaded payment evidence file (receipt, confirmation, etc.) */
  paymentFileUrl?: string;
  /** Name of the uploaded payment file */
  paymentFileName?: string;
  /** User ID of who marked the visit as paid */
  paymentMarkedBy?: string;
  /** Notes about the payment */
  paymentNotes?: string;

  // Monitor payment tracking
  /** Payment status for the monitor/supervisor */
  monitorPaymentStatus?: VisitPaymentStatus;
  /** Date when monitor was paid */
  monitorPaymentDate?: DateField;
  /** User ID of who marked the monitor as paid */
  monitorPaymentMarkedBy?: string;
  /** Notes about the monitor payment */
  monitorPaymentNotes?: string;
}

export type VisitStatus = EventType |
  "pending"
  | "pending_confirmation"
  | "scheduled"
  | "canceled"
  | "no_show"
  | "completed";

export type VisitPaymentStatus = 'unpaid' | 'paid';

export const VISIT_PAYMENT_STATUS_LABELS: Record<VisitPaymentStatus, string> = {
  unpaid: 'Unpaid',
  paid: 'Paid',
};

export const VISIT_STATUS_LABELS: Record<VisitStatus, string> = {
  pending: "Pending",
  pending_confirmation: "Awaiting Confirmation",
  scheduled: "Scheduled",
  canceled: "Canceled",
  supervisor_enroute: "Supervisor En Route",
  supervisor_arrived: "Supervisor Arrived",
  supervisor_started: "Supervisor Started",
  supervisor_ended: "Supervisor Ended",
  supervisor_no_show: "Supervisor No Show",
  no_show: "No Show",
  observation: "Observation",
  interaction: "Interaction",
  incident: "Incident",
  intervention: "Intervention",
  phone_call: "Phone Call",
  party_arrived: "Arrival",
  handoff_note: "Handoff Notes",
  completed: "Completed",
};

// ============================================================================
// EVENT
// ============================================================================

export interface EventLocation {
  latitude: number;
  longitude: number;
}

/** Who a logged phone call was with, and its direction. */
export type PhoneCallParty = 'visiting_parent' | 'custodial_parent' | 'parent' | 'supervisor' | 'other';
export type PhoneCallDirection = 'incoming' | 'outgoing';

export const PHONE_CALL_PARTY_LABELS: Record<PhoneCallParty, string> = {
  visiting_parent: 'Visiting Parent',
  custodial_parent: 'Custodial Parent',
  parent: 'Parent',
  supervisor: 'Supervisor',
  other: 'Other',
};

export const PHONE_CALL_DIRECTION_LABELS: Record<PhoneCallDirection, string> = {
  incoming: 'Incoming',
  outgoing: 'Outgoing',
};

/**
 * Who arrived, for a party_arrived event. Texas (and other) courts require the
 * non-custodial parent's arrival time recorded separately from the custodial
 * parent's — "Start Visit" alone doesn't capture that.
 */
export type ArrivalParty = 'non_custodial_parent' | 'custodial_parent' | 'child' | 'other';

export const ARRIVAL_PARTY_LABELS: Record<ArrivalParty, string> = {
  non_custodial_parent: 'Non-Custodial Parent',
  custodial_parent: 'Custodial Parent',
  child: 'Child',
  other: 'Other',
};

export interface Event extends BaseEntity {
  /**
   * Owning visit. Optional because some events (e.g. phone calls logged from
   * the case screen) are case-scoped and not tied to a specific visit.
   */
  visitId?: string;
  accountId: string;
  supervisorId?: string;
  caseId?: string;
  type: EventType;
  timestamp?: DateField;
  location?: Coordinates | EventLocation;
  body?: string;
  notes?: string;
  subtype?: string | null;
  severity?: string;
  actionTaken?: string | null;
  images?: string[]
  /** For type === "phone_call": who the call was with. */
  callWith?: PhoneCallParty;
  /** For type === "phone_call": incoming or outgoing. */
  callDirection?: PhoneCallDirection;
  /** For type === "party_arrived": which party this arrival records. */
  arrivalParty?: ArrivalParty;
  metadata?: Record<string, unknown>;
  createdBy: string;
  updatedBy?: string;
}

// ============================================================================
// CHECK-IN
// ============================================================================

export interface Checkin extends BaseEntity {
  visitId: string;
  supervisorId: string;
  type: CheckinType;
  timestamp: DateField;
  location?: Coordinates;
  notes?: string;
  metadata?: Record<string, unknown>;
}

export type CheckinType = 'start' | 'end' | 'break' | 'resume' | 'arrival' | 'departure';

// ============================================================================
// VISIT SCHEDULE
// ============================================================================

export interface VisitSchedule extends BaseEntity {
  caseId: string;
  accountId: string;
  supervisorId: string;
  recurring: boolean;
  pattern?: RecurringPattern;
  exceptions: ScheduleException[];
}

export interface RecurringPattern {
  frequency: RecurringFrequency;
  interval: number;
  daysOfWeek?: number[]; // 0 = Sunday, 1 = Monday, etc.
  dayOfMonth?: number;
  endDate?: DateField;
  maxOccurrences?: number;
}

export type RecurringFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface ScheduleException {
  date: DateField;
  type: ExceptionType;
  reason?: string;
  alternativeTime?: DateField;
}

export type ExceptionType = 'canceled' | 'rescheduled' | 'modified';

// ============================================================================
// VISIT TEMPLATE
// ============================================================================

export interface VisitTemplate extends BaseEntity {
  name: string;
  accountId: string;
  description?: string;
  duration: number; // in minutes
  locationType: LocationType;
  requiredEvents: EventType[];
  optionalEvents: EventType[];
  notes?: string;
  isActive: boolean;
}

// ============================================================================
// VISIT SUMMARY
// ============================================================================

export interface VisitSummary extends BaseEntity {
  caseId: string;
  accountId: string;
  supervisorId: string;
  startDate: DateField;
  endDate: DateField;
  status: VisitStatus;
  duration: number;
  distance?: number;
  eventCount: number;
  imageCount: number;
  hasReport: boolean;
  hasInvoice: boolean;
  supervisor: {
    id: string;
    firstName: string;
    lastName: string;
  };
  location: {
    name?: string;
    formattedAddress: string;
  };
}

// ============================================================================
// VISIT STATISTICS
// ============================================================================

export interface VisitStatistics extends BaseEntity {
  accountId: string;
  timeFrame: VisitTimeFrame;
  totalVisits: number;
  completedVisits: number;
  canceledVisits: number;
  noShowVisits: number;
  averageDuration: number;
  totalDistance: number;
  totalRevenue: number;
  currency: string;
  supervisorStats: SupervisorVisitStats[];
  locationStats: LocationVisitStats[];
}

export type VisitTimeFrame = 'day' | 'week' | 'month' | 'quarter' | 'year';

export interface SupervisorVisitStats {
  supervisorId: string;
  supervisorName: string;
  totalVisits: number;
  completedVisits: number;
  averageDuration: number;
  totalRevenue: number;
}

export interface LocationVisitStats {
  locationId: string;
  locationName: string;
  totalVisits: number;
  averageDuration: number;
}

// ============================================================================
// VISIT REQUEST
// ============================================================================

export interface VisitRequest extends BaseEntity {
  caseId: string;
  accountId: string;
  requestedBy: string;
  requestedSupervisorId?: string;
  preferredStartDate: DateField;
  preferredEndDate: DateField;
  location: Location;
  notes?: string;
  urgency: UrgencyLevel;
  status: RequestStatus;
  approvedBy?: string;
  approvedAt?: DateField;
  visitId?: string;
}

export type UrgencyLevel = 'low' | 'medium' | 'high' | 'urgent';
export type RequestStatus = 'pending' | 'approved' | 'rejected' | 'canceled';

// ============================================================================
// VISIT CONFLICT
// ============================================================================

export interface VisitConflict extends BaseEntity {
  visitId: string;
  conflictingVisitId: string;
  type: ConflictType;
  severity: ConflictSeverity;
  description: string;
  resolved: boolean;
  resolvedBy?: string;
  resolvedAt?: DateField;
  resolution?: string;
}

export type ConflictType = 'supervisor_overlap' | 'location_overlap' | 'time_overlap' | 'resource_conflict';
export type ConflictSeverity = 'low' | 'medium' | 'high' | 'critical';
